import React, { useState, useContext } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import LanguageIcon from "@mui/icons-material/Language";
import { Link } from "react-router-dom";
import logo from "../../assets/logo.jpg";
import { AuthContext } from "../../context/AuthContext";
import { LanguageContext } from "../../context/LanguageContext"; // Import LanguageContext

// Define navigation items with both English and Urdu labels
const navItems = [
  { label: "Home", urLabel: "ہوم", to: "/" },
  { label: "Gallery", urLabel: "گیلری", to: "/Gallery" },
  { label: "Videos", urLabel: "ویڈیوز", to: "/Videos" },
  { label: "Posts", urLabel: "پوسٹس", to: "/Posts" },
  { label: "About", urLabel: "متعلق", to: "/about" },
  { label: "Resources", urLabel: "وسائل", to: "/Resources" }, 
  {
    label: "Education in Pakistan",
    urLabel: "پاکستان میں تعلیم",
    href: "https://educationinpakistan.com/",
  },
  { label: "Login", urLabel: "لاگ ان", to: "/login" },
  { label: "ADMIN", urLabel: "ایڈمن", to: "/admin" },
  { label: "Log Out", urLabel: "لاگ آؤٹ", to: "/" },
];

export default function Navbar() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { isLoggedIn, setIsLoggedIn } = useContext(AuthContext);
  const { language, toggleLanguage } = useContext(LanguageContext); // Consume LanguageContext

  const handleToggleDrawer = () => setDrawerOpen(!drawerOpen);

  const user = JSON.parse(localStorage.getItem("user"));

  const handleLogout = () => {
    setIsLoggedIn(false);
    sessionStorage.removeItem("isLoggedIn");
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    sessionStorage.clear();
    console.log("Logged out");
  };

  const renderNavItem = (item, index, isDrawer = false) => {
    // Determine the label based on the current language
    const currentLabel = language === "en" ? item.label : item.urLabel;

    // Conditional logic
    if (item.label === "Login" && isLoggedIn) return null;
    if (item.label === "ADMIN" && !isLoggedIn) return null;
    if (item.label === "ADMIN" && user && user.role === "user") return null;
    if (item.label === "Log Out" && !isLoggedIn) return null;

    const sharedProps = {
      key: index,
      sx: {
        color: "black",
        "&:hover": { backgroundColor: "#f9a825" },
        "&.Mui-selected, &.Mui-selected:hover": {
          backgroundColor: "transparent",
          color: "black",
        },
      },
    };

    // External link
    if (item.href) {
      return isDrawer ? (
        <ListItem
          component="a"
          href={item.href}
          target="_blank"
          {...sharedProps}
        >
          <Box sx={{ textAlign: "center", width: "100%" }}>
            <ListItemText primary={currentLabel} sx={{ color: "white" }} />
          </Box>
        </ListItem>
      ) : (
        <Button
          key={index}
          component="a"
          href={item.href}
          target="_blank"
          data-tts="true"
          sx={{
            textTransform: "none",
            color: "#f9a825",
            fontWeight: 700,
            fontSize: "18px",
          }}
        >
          {currentLabel}
        </Button>
      );
    }

    // Log Out
    if (item.label === "Log Out") {
      return isDrawer ? (
        <ListItem
          component={Link}
          to={item.to}
          onClick={handleLogout}
          {...sharedProps}
        >
          <Box sx={{ textAlign: "center", width: "100%" }}>
            <ListItemText primary={currentLabel} sx={{ color: "white" }} />
          </Box>
        </ListItem>
      ) : (
        <Button
          key={index}
          onClick={handleLogout}
          component={Link}
          data-tts="true"
          to={item.to}
          sx={{
            textTransform: "none",
            color: "#f9a825",
            fontWeight: 700,
            fontSize: "18px",
          }}
        >
          {currentLabel}
        </Button>
      );
    }

    // Regular internal link
    return isDrawer ? (
      <ListItem component={Link} to={item.to} {...sharedProps}>
        <Box sx={{ textAlign: "center", width: "100%" }}>
          <ListItemText primary={currentLabel} sx={{ color: "white" }} />
        </Box>
      </ListItem>
    ) : (
      <Button
        key={index}
        component={Link}
        data-tts="true"
        to={item.to}
        sx={{
          textTransform: "none",
          color: "#f9a825",
          fontWeight: 700,
          fontSize: "18px",
        }}
      >
        {currentLabel}
      </Button>
    );
  };

  return (
    <AppBar
      position="static"
      sx={{ backgroundColor: "transparent", boxShadow: "none", color: "#fff" }}
    >
      <Toolbar>
        {/* Logo */}
        <Box
          component={Link}
          to="/"
          sx={{
            display: "flex",
            alignItems: "center",
            textDecoration: "none",
            flexGrow: 1,
            gap: 1,
          }}
        >
          <img src={logo} alt="logo" style={{ height: 40 }} />
          <Typography
            sx={{ color: "#f9a825", fontWeight: "bold", fontSize: "18px" }}
          >
            ISSAD
          </Typography>
        </Box>

        {/* Responsive nav */}
        {isMobile ? (
          <>
            <IconButton onClick={handleToggleDrawer} sx={{ color: "#f9a825" }}>
              <MenuIcon />
            </IconButton>
            <Drawer
              anchor="right"
              open={drawerOpen}
              onClose={handleToggleDrawer}
              slotProps={{
                paper: {
                  sx: {
                    backgroundColor: "rgba(255,255,255,0.4)",
                    backdropFilter: "blur(8px)",
                    boxShadow: "none",
                  },
                },
              }}
            >
              <Box
                sx={{ width: "100vw" }}
                role="presentation"
                onClick={handleToggleDrawer}
              >
                <Box sx={{ display: "flex", justifyContent: "flex-end", p: 2 }}>
                  <IconButton
                    onClick={handleToggleDrawer}
                    sx={{ color: "black" }}
                  >
                    ✕
                  </IconButton>
                </Box>

                <List>
                  {navItems.map((item, index) =>
                    renderNavItem(item, index, true)
                  )}
                  {/* Language Toggle in Drawer */}
                  <ListItem
                    onClick={toggleLanguage}
                    sx={{ textAlign: "center", width: "100%" }}
                  >
                    <Box sx={{ textAlign: "center", width: "100%" }}>
                      <ListItemText
                        primary={language === "en" ? "اردو" : "English"}
                        sx={{ color: "white" }}
                      />
                    </Box>
                  </ListItem>
                </List>
              </Box>
            </Drawer>
          </>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {navItems.map((item, index) => renderNavItem(item, index, false))}

            <Link to="/search">
              <IconButton
                sx={{ color: "#f9a825" }}
                data-tts="true"
                data-tts-text="Search"
              >
                <SearchIcon />
              </IconButton>
            </Link>

            <IconButton
              sx={{ color: "#f9a825" }}
              data-tts="true"
              data-tts-text="Text-To-Speech"
              onClick={() => {
                const current = localStorage.getItem("TTS") === "true";
                localStorage.setItem("TTS", !current);
              }}
            >
              <VolumeUpIcon />
            </IconButton>

            <Button
              onClick={toggleLanguage}
              data-tts="true"
              data-tts-text="Language"
              sx={{
                textTransform: "none",
                color: "#f9a825",
                fontWeight: 700,
                fontSize: "18px",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <LanguageIcon />
              {language !== "en" ? "اردو" : "EN"}
            </Button>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
}